import React, {FormEvent, KeyboardEvent, Suspense, useEffect, useRef, useState} from 'react';
import {Box, Chip, CircularProgress, IconButton, Menu, MenuItem, Stack, TextField, Tooltip, Typography, useMediaQuery} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import CloseIcon from '@mui/icons-material/Close';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import {useSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';
import type {RagAttachmentDTO} from 'crqlar-react-common/dist/models/ai/ragChat.model';
import {
    getImageFileFromTransfer,
    getSupportedMarkdownImagePaste,
    transferHasFiles,
} from 'crqlar-react-common/dist/utils/markdownImage.util';
import {useScreenshotCapture} from '../screenshot/useScreenshotCapture';
import {usePendingScreenshots} from './usePendingScreenshots';

const AssistantScreenshotFullscreen = React.lazy(() => import('../screenshot/AssistantScreenshotFullscreen'));

const COMPOSER_BORDER_WIDTH = 1.5;
const COMPOSER_BORDER_RADIUS = 12;

interface SubmitOptions {
    attachments?: RagAttachmentDTO[];
    humanHandoff?: boolean;
}

interface Props {
    onSubmit: (text: string, options?: SubmitOptions) => void;
    disabled?: boolean;
    /** An AI answer is in flight — the send button becomes a Stop button. */
    asking?: boolean;
    /** Stop waiting on the in-flight answer. */
    onStop?: () => void;
    conversationId?: string | null;
    /** The team owns this conversation (handoff WAITING / ACTIVE), so the server sends every
     *  message their way whatever the composer does. Presentation only — it changes what the
     *  composer says, never what it sends. */
    teamOwnsConversation?: boolean;
    shouldFocus?: boolean;
    focusToken?: number;
}

const AssistantComposer: React.FC<Props> = ({
    onSubmit,
    disabled,
    asking,
    onStop,
    conversationId,
    teamOwnsConversation,
    shouldFocus,
    focusToken,
}) => {
    const [question, setQuestion] = useState('');
    const [askTeam, setAskTeam] = useState(false);
    const [toolMenuAnchor, setToolMenuAnchor] = useState<HTMLElement | null>(null);
    const [shineActive, setShineActive] = useState(false);
    const [composerSize, setComposerSize] = useState({width: 0, height: 0});
    const composerRef = useRef<HTMLFormElement | null>(null);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
    const shineBorderId = `assistant-composer-shine-${React.useId().replace(/:/g, '')}`;
    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation();
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const pendingScreenshots = usePendingScreenshots();

    // tracks which existing screenshot a capture is re-editing — set before
    // opening the editor, read on completion. undefined means "add as new".
    const replacingIdRef = useRef<string | undefined>(undefined);

    const capture = useScreenshotCapture({
        onComplete: blob => {
            const replacingId = replacingIdRef.current;
            replacingIdRef.current = undefined;
            pendingScreenshots.addOrReplace(blob, replacingId);
        },
        onError: message => enqueueSnackbar(message, {variant: 'error'}),
    });

    // Reset transient tool selections (e.g. the "Ask team" chip) when
    // the conversation changes — but keep the draft text + screenshots
    // so the user doesn't lose work after starting a new chat.
    useEffect(() => {
        setAskTeam(false);
    }, [conversationId]);

    // Whether this message reaches a person: because the user asked for one, or because the team
    // already has the conversation and the server routes it there regardless. Wording and the
    // AI-only Stop control read this; only the explicit tick is ever sent (see `submit`).
    const goesToTeam = askTeam || !!teamOwnsConversation;

    useEffect(() => {
        if (!shouldFocus || disabled) return;
        const timer = window.setTimeout(() => {
            const input = inputRef.current;
            if (!input) return;
            input.focus({preventScroll: true});
            const cursorPosition = input.value.length;
            input.setSelectionRange(cursorPosition, cursorPosition);
        }, 0);
        return () => window.clearTimeout(timer);
    }, [shouldFocus, focusToken, disabled]);

    useEffect(() => {
        if (!shouldFocus || disabled) setShineActive(false);
    }, [shouldFocus, disabled]);

    useEffect(() => {
        const element = composerRef.current;
        if (!element || typeof ResizeObserver === 'undefined') return;

        const updateComposerSize = () => {
            const {width, height} = element.getBoundingClientRect();
            const nextSize = {
                width: Math.round(width * 100) / 100,
                height: Math.round(height * 100) / 100,
            };

            setComposerSize(current => (
                current.width === nextSize.width && current.height === nextSize.height
                    ? current
                    : nextSize
            ));
        };

        updateComposerSize();
        const observer = new ResizeObserver(updateComposerSize);
        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    const submit = async () => {
        const trimmed = question.trim();
        if (!trimmed || disabled || pendingScreenshots.uploading) return;

        // Only ever the user's explicit tick: it opens a handoff on a conversation the assistant
        // still owns. Once the team has it, the thread's own state routes every message to them —
        // the flag is not something the client has to keep re-asserting.
        const humanHandoff = askTeam || undefined;

        // upload any pending screenshots first; only commit if all succeed
        let attachments: RagAttachmentDTO[] | undefined;
        try {
            attachments = await pendingScreenshots.upload();
        } catch {
            enqueueSnackbar(t('Screenshot upload failed.'), {variant: 'error'});
            return;
        }

        // clear local state — uploaded URLs are now the source of truth
        pendingScreenshots.clear();
        setQuestion('');
        setAskTeam(false);
        onSubmit(trimmed, {attachments, humanHandoff});
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        void submit();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void submit();
        }
    };

    const handleRemoveScreenshot = (id: string) => {
        pendingScreenshots.remove(id);
    };

    const handleEditScreenshot = (id: string) => {
        const target = pendingScreenshots.items.find(s => s.id === id);
        if (!target?.blob) return;
        pendingScreenshots.setPreviewId(null);
        replacingIdRef.current = id;
        capture.editImage(target.blob);
    };

    const addTransferredImage = (transfer: DataTransfer) => {
        const image = getSupportedMarkdownImagePaste(transfer);
        const unsupportedImageFile = !image ? getImageFileFromTransfer(transfer) : undefined;

        if (!image && !unsupportedImageFile) return false;

        if (disabled || pendingScreenshots.uploading || capture.active) {
            enqueueSnackbar(t('Wait for the current screenshot action to finish.'), {variant: 'warning'});
            return true;
        }

        if (!image) {
            enqueueSnackbar(t('Only pasted PNG and JPG images are supported.'), {variant: 'warning'});
            return true;
        }

        if (image.type === 'url') {
            pendingScreenshots.addImageUrl(image.url);
        } else {
            pendingScreenshots.addFile(image.file);
        }

        enqueueSnackbar(t('Image added.'), {variant: 'success'});
        return true;
    };

    const handlePasteCapture = (event: React.ClipboardEvent<HTMLFormElement>) => {
        if (!addTransferredImage(event.clipboardData)) return;

        event.preventDefault();
        event.stopPropagation();
    };

    const handleDropCapture = (event: React.DragEvent<HTMLFormElement>) => {
        if (!addTransferredImage(event.dataTransfer)) return;

        event.preventDefault();
        event.stopPropagation();
    };

    const handleDragOverCapture = (event: React.DragEvent<HTMLFormElement>) => {
        if (transferHasFiles(event.dataTransfer)) event.preventDefault();
    };

    const canSubmit = !!question.trim() && !disabled && !pendingScreenshots.uploading;
    const previewScreenshot = pendingScreenshots.preview;

    // Forgiving click target: clicks that land on padding, gutters,
    // or empty space inside the composer wrapper still move focus into
    // the textarea. We bail when the user actually clicked a real
    // control (button / link / input) so those work normally.
    const focusInputFromWrapperClick = (e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        if (target.closest('button, a, input, textarea, [role="button"]')) return;
        e.preventDefault();
        inputRef.current?.focus();
    };

    return (
        <Box sx={{px: 1.5, pt: 1, pb: 1}}>
            <Box
                component="form"
                ref={composerRef}
                onSubmit={handleSubmit}
                onPasteCapture={handlePasteCapture}
                onDropCapture={handleDropCapture}
                onDragOverCapture={handleDragOverCapture}
                onFocusCapture={() => setShineActive(true)}
                onBlurCapture={event => {
                    const nextFocusedElement = event.relatedTarget as Node | null;
                    if (!nextFocusedElement || !event.currentTarget.contains(nextFocusedElement)) {
                        setShineActive(false);
                    }
                }}
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    isolation: 'isolate',
                    border: 0,
                    borderRadius: `${COMPOSER_BORDER_RADIUS}px`,
                    bgcolor: '#fff',
                    boxShadow: 'inset 0 0 0 1px #d0d0d0',
                    px: 1.25,
                    pt: 1,
                    // matches px so the +/send buttons sit the same distance
                    // from the bottom edge as from the sides
                    pb: 1.25,
                    transition: prefersReducedMotion ? 'none' : 'box-shadow 150ms ease',
                    '& > :not(.assistant-composer-shine-border)': {
                        position: 'relative',
                        zIndex: 1,
                    },
                    '&:focus-within': {
                        boxShadow: 'none',
                        '& .assistant-composer-shine-border': {
                            opacity: 1,
                        },
                    },
                }}
            >
                <Box
                    component="svg"
                    className="assistant-composer-shine-border"
                    aria-hidden
                    focusable="false"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        opacity: 0,
                        transition: prefersReducedMotion ? 'none' : 'opacity 150ms ease',
                        zIndex: 0,
                    }}
                >
                    <defs>
                        <linearGradient
                            id={shineBorderId}
                            gradientUnits="userSpaceOnUse"
                            x1={-composerSize.width}
                            y1={-composerSize.height}
                            x2={composerSize.width * 2}
                            y2={composerSize.height * 2}
                            spreadMethod="repeat"
                        >
                            <stop offset="0%" stopColor="#A07CFE"/>
                            <stop offset="45%" stopColor="#FE8FB5"/>
                            <stop offset="75%" stopColor="#FFBE7B"/>
                            <stop offset="100%" stopColor="#A07CFE"/>
                            {shineActive && !prefersReducedMotion && (
                                <animateTransform
                                    attributeName="gradientTransform"
                                    type="translate"
                                    from={`-${composerSize.width} -${composerSize.height}`}
                                    to={`${composerSize.width * 2} ${composerSize.height * 2}`}
                                    dur="9s"
                                    repeatCount="indefinite"
                                />
                            )}
                        </linearGradient>
                    </defs>
                    {composerSize.width > COMPOSER_BORDER_WIDTH && composerSize.height > COMPOSER_BORDER_WIDTH && (
                        <rect
                            x={COMPOSER_BORDER_WIDTH / 2}
                            y={COMPOSER_BORDER_WIDTH / 2}
                            width={composerSize.width - COMPOSER_BORDER_WIDTH}
                            height={composerSize.height - COMPOSER_BORDER_WIDTH}
                            rx={COMPOSER_BORDER_RADIUS - (COMPOSER_BORDER_WIDTH / 2)}
                            ry={COMPOSER_BORDER_RADIUS - (COMPOSER_BORDER_WIDTH / 2)}
                            fill="none"
                            stroke={`url(#${shineBorderId})`}
                            strokeWidth={COMPOSER_BORDER_WIDTH}
                        />
                    )}
                </Box>
                {/* Forgiving click area: padding + gaps around the text
                    input focus the textarea on click. Scoped to the top
                    region only so the action-button row below keeps its
                    default cursor and click semantics. */}
                <Box
                    onMouseDown={focusInputFromWrapperClick}
                    sx={{
                        cursor: 'text',
                        // bleed into the form's padding so clicks in the
                        // top + left/right gutters still focus the input.
                        // content stays put via matching positive padding.
                        mx: -1.25,
                        mt: -1,
                        px: 1.25,
                        pt: 1,
                        pb: 0.5,
                    }}
                >
                <TextField
                    inputRef={inputRef}
                    multiline
                    minRows={2}
                    maxRows={6}
                    fullWidth
                    variant="standard"
                    placeholder={goesToTeam ? t('Message the Crqlar team') : t('Ask Crqlar Assistant')}
                    value={question}
                    onChange={e => setQuestion(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    InputProps={{disableUnderline: true}}
                    sx={{'& .MuiInputBase-input': {fontSize: 14, lineHeight: 1.45}}}
                />

                {pendingScreenshots.items.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{mt: 1}}>
                        {pendingScreenshots.items.map(s => (
                            <Box
                                key={s.id}
                                sx={{
                                    position: 'relative',
                                    width: 110,
                                    maxWidth: '100%',
                                }}
                            >
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => pendingScreenshots.setPreviewId(s.id)}
                                    aria-label={t('Open screenshot preview')}
                                    sx={theme => ({
                                        all: 'unset',
                                        cursor: 'pointer',
                                        display: 'block',
                                        width: '100%',
                                        borderRadius: 2.5,
                                        overflow: 'hidden',
                                        border: '1px solid #e0e0e0',
                                        bgcolor: '#fafafa',
                                        transition: 'border-color 120ms ease',
                                        '&:hover': {borderColor: theme.palette.primary.main},
                                        '&:focus-visible': {
                                            outline: `2px solid ${theme.palette.primary.main}`,
                                            outlineOffset: 2,
                                        },
                                    })}
                                >
                                    <Box
                                        component="img"
                                        src={s.url}
                                        alt={t('Screenshot')}
                                        sx={{
                                            display: 'block',
                                            width: '100%',
                                            height: 70,
                                            objectFit: 'contain',
                                            bgcolor: '#fff',
                                        }}
                                    />
                                </Box>
                                <Tooltip title={t('Remove screenshot')}>
                                    <IconButton
                                        size="small"
                                        disabled={pendingScreenshots.uploading}
                                        onClick={e => {
                                            e.stopPropagation();
                                            handleRemoveScreenshot(s.id);
                                        }}
                                        aria-label={t('Remove screenshot')}
                                        sx={{
                                            position: 'absolute',
                                            top: -6,
                                            right: -6,
                                            width: 20,
                                            height: 20,
                                            bgcolor: 'rgba(0,0,0,0.7)',
                                            color: '#fff',
                                            border: '2px solid #fff',
                                            boxSizing: 'border-box',
                                            '&:hover': {bgcolor: 'rgba(0,0,0,0.85)'},
                                        }}
                                    >
                                        <CloseIcon sx={{fontSize: 12}}/>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ))}
                    </Stack>
                )}
                </Box>

                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        // bleed into the form's bottom + side padding so
                        // the blue region meets the yellow with no gap.
                        mx: -1.25,
                        mb: -1.25,
                        px: 1.25,
                        pb: 1.25,
                    }}
                >
                    <Tooltip title={t('Add tools')}>
                        <span>
                            <IconButton
                                type="button"
                                size="small"
                                disabled={disabled || pendingScreenshots.uploading || capture.active}
                                aria-label={t('Add tools')}
                                onClick={e => setToolMenuAnchor(e.currentTarget)}
                                sx={{color: 'text.secondary'}}
                            >
                                <AddIcon fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Menu
                        anchorEl={toolMenuAnchor}
                        open={!!toolMenuAnchor}
                        onClose={() => setToolMenuAnchor(null)}
                        anchorOrigin={{horizontal: 'left', vertical: 'top'}}
                        transformOrigin={{horizontal: 'left', vertical: 'bottom'}}
                    >
                        <MenuItem
                            onClick={() => {
                                setToolMenuAnchor(null);
                                replacingIdRef.current = undefined;
                                capture.beginCapture();
                            }}
                            sx={{fontSize: 14}}
                        >
                            <AddPhotoAlternateOutlinedIcon
                                fontSize="small"
                                sx={{mr: 1, color: 'text.secondary'}}
                            />
                            {t('Add screenshot')}
                        </MenuItem>
                        <MenuItem
                            disabled={goesToTeam}
                            onClick={() => {
                                setToolMenuAnchor(null);
                                setAskTeam(true);
                            }}
                            sx={{fontSize: 14}}
                        >
                            <SupportAgentOutlinedIcon
                                fontSize="small"
                                sx={{mr: 1, color: 'text.secondary'}}
                            />
                            {t('Ask team')}
                        </MenuItem>
                    </Menu>

                    {/* Only the user's own tick gets a chip — a conversation the team already owns
                        says so in the banner above, and repeating it here would just be noise. */}
                    {askTeam && (
                        <Chip
                            size="small"
                            icon={<SupportAgentOutlinedIcon sx={{fontSize: 14}}/>}
                            label={t('Ask team')}
                            onDelete={() => setAskTeam(false)}
                            deleteIcon={<CloseIcon sx={{fontSize: 14}}/>}
                            sx={{
                                ml: 0.5,
                                height: 24,
                                fontSize: 12,
                                fontWeight: 600,
                                bgcolor: 'rgba(237, 108, 2, 0.12)',
                                color: '#ed6c02',
                                '& .MuiChip-icon': {color: '#ed6c02'},
                                '& .MuiChip-deleteIcon': {color: '#ed6c02'},
                            }}
                        />
                    )}

                    <Box sx={{flex: 1}}/>

                    {/* Stop belongs to a running generation; a message to a person has nothing to cancel. */}
                    {asking && onStop && !goesToTeam ? (
                        <Tooltip title={t('Stop')}>
                            <IconButton
                                type="button"
                                size="small"
                                onClick={onStop}
                                aria-label={t('Stop generating')}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '&:hover': {bgcolor: 'primary.dark'},
                                }}
                            >
                                <StopRoundedIcon sx={{fontSize: 20}}/>
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Tooltip
                            title={
                                pendingScreenshots.uploading
                                    ? t('Uploading screenshots…')
                                    : disabled
                                        ? t('Waiting for response…')
                                        : t('Send · Enter')
                            }
                        >
                            <span>
                                <IconButton
                                    type="submit"
                                    size="small"
                                    disabled={!canSubmit}
                                    aria-label={t('Send')}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        '&:hover': {bgcolor: 'primary.dark'},
                                        '&.Mui-disabled': {bgcolor: '#e0e0e0', color: '#9e9e9e'},
                                    }}
                                >
                                    {pendingScreenshots.uploading
                                        ? <CircularProgress size={16} sx={{color: '#fff'}}/>
                                        : <ArrowUpwardIcon sx={{fontSize: 18}}/>}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Stack>
            </Box>

            <Typography
                variant="caption"
                color="text.secondary"
                sx={{display: 'block', textAlign: 'center', mt: 0.75, fontSize: '0.7rem'}}
            >
                {t('Crqlar Assistant can make mistakes. Verify important info.')}
            </Typography>

            {capture.overlay}

            {previewScreenshot && (
                <Suspense fallback={null}>
                        <AssistantScreenshotFullscreen
                            imageUrl={previewScreenshot.url}
                            onEdit={previewScreenshot.blob ? () => handleEditScreenshot(previewScreenshot.id) : undefined}
                            onDelete={() => handleRemoveScreenshot(previewScreenshot.id)}
                            onClose={() => pendingScreenshots.setPreviewId(null)}
                        />
                </Suspense>
            )}
        </Box>
    );
};

export default AssistantComposer;
